import { FC, JSX } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { NavigationItem } from "../../types/components.d";
import { SimplifiedCompanyStatus } from "../../utils/authUtils";

export interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  companyStatus: SimplifiedCompanyStatus["status"] | null;
  jobPostCount: number | null; // New prop for Company job post count
  pendingCompaniesCount?: number; // New prop for Agent pending companies count
  companyApplicationsCount?: number; // New prop for Company applications count
  isjobseekerProfileCompleted?: boolean; // New prop for Company applications count
  jobseekerApplicationsCount?: number; // New prop for Jobseeker applications count
  agentApplicationsCount?: number; // New prop for Agent applications count
}
const Sidebar: FC<SidebarProps> = ({
  isOpen,
  onClose,
  companyStatus,
  jobPostCount,
  pendingCompaniesCount,
  companyApplicationsCount,
  isjobseekerProfileCompleted,
  jobseekerApplicationsCount,
  agentApplicationsCount,
}): JSX.Element => {
  const { user } = useAuth();
  const location = useLocation();

  const getNavigationItems = (): NavigationItem[] => {
    if (!user) return [];

    switch (user.role) {
      case "Jobseeker":
        return isjobseekerProfileCompleted
          ? [
              {
                name: "Dashboard",
                href: "/user/dashboard",
                icon: "M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z",
                badge: null,
              },
              {
                name: "Applications",
                href: "/user/status",
                icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
                badge: jobseekerApplicationsCount
                  ? jobseekerApplicationsCount > 0
                    ? jobseekerApplicationsCount
                    : null
                  : null,
              },
              {
                name: "Profile",
                href: "/user/profile",
                icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
                badge: null,
              },
            ]
          : [
              {
                name: "Complete Profile",
                href: "/user/profile/complete",
                icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
                badge: null,
              },
            ];
      case "Intern":
        return isjobseekerProfileCompleted
          ? [
              {
                name: "Dashboard",
                href: "/user/dashboard",
                icon: "M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z",
                badge: null,
              },
              {
                name: "Applications",
                href: "/user/status",
                icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
                badge: jobseekerApplicationsCount
                  ? jobseekerApplicationsCount > 0
                    ? jobseekerApplicationsCount
                    : null
                  : null,
              },
              {
                name: "Profile",
                href: "/user/profile",
                icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
                badge: null,
              },
            ]
          : [
              {
                name: "Complete Profile",
                href: "/user/profile/complete",
                icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
                badge: null,
              },
            ];
      case "Company":
        return companyStatus == "NotRegistered"
          ? [
              {
                name: "Company Registration",
                href: "/company/register",
                icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
                badge: null,
              },
            ]
          : [
              {
                name: "Dashboard",
                href: "/company/dashboard",
                icon: "M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z",
                badge: null,
              },
              {
                name: "List Job",
                href: "/company/jobs",
                icon: "M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6",
                badge: jobPostCount ? jobPostCount > 0 ? jobPostCount : null : null,
              },
              {
                name: "Applications",
                href: "/company/applications",
                icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
                badge: companyApplicationsCount ? companyApplicationsCount > 0 ? companyApplicationsCount : null : null,
              },
              {
                name: "Company Profile",
                href: "/company/profile",
                icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4",
                badge: null,
              },
            ];
      case "Agent":
        return [
          {
            name: "Dashboard",
            href: "/agent/dashboard",
            icon: "M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z",
            badge: null,
          },
          // {
          //   name: "Onboarding",
          //   href: "/agent/onboarding",
          //   icon: "M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253",
          //   badge: null,
          // },
          {
            name: "Pending Companies",
            href: "/agent/pending",
            icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
            badge: pendingCompaniesCount ? pendingCompaniesCount > 0 ? pendingCompaniesCount : null : null,
          },
          {
            name: "All Companies",
            href: "/agent/companies",
            icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4",
            badge: null,
          },
          {
            name: "All Posts",
            href: "/agent/posts",
            icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
            badge: null,
          },
          {
            name: "All Users",
            href: "/agent/users",
            icon: "M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695L12 22.5l-7.812-1.7a.75.75 0 01-.437-.695z",
            badge: null,
          },
          // {
          //   name: "External Candidates",
          //   href: "/agent/external-candidates",
          //   icon: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z",
          //   badge: null,
          // },
          {
            name: "Broadcast Email",
            href: "/agent/BroadcastEmail",
            icon: "M 2.25 6.75 L 21.75 6.75 M 2.25 6.75 V 17.25 C 2.25 17.8467 2.487 18.419 2.9018 18.8337 C 3.3165 19.2485 3.8888 19.4855 4.4855 19.4855 H 19.5145 C 20.1112 19.4855 20.6835 19.2485 21.0982 18.8337 C 21.513 18.419 21.75 17.8467 21.75 17.25 V 6.75 C 22 5 2 5 2.25 6.75 L 12 15.75 L 21.75 6.75",
            badge: null,
          },
          {
            name: "Inquiries",
            href: "/agent/Inquiries",
            icon: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9",
            badge: null,
          },
          // {
          //   name: "Manage Applications",
          //   href: "/agent/manage",
          //   icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
          //   badge: agentApplicationsCount ? agentApplicationsCount > 0 ? agentApplicationsCount : null : null,
          // },
          // {
          //   name: "Reports",
          //   href: "/agent/reports",
          //   icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
          //   badge: null,
          // },
          // {
          //   name: "Profile",
          //   href: "/agent/profile",
          //   icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
          //   badge: null,
          // },
        ];
      
      case "Admin":
        return [
          {
            name: "Dashboard",
            href: "/admin/dashboard",
            icon: "M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z",
            badge: null,
          },
          // {
          //   name: "Onboarding",
          //   href: "/admin/onboarding",
          //   icon: "M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253",
          //   badge: null,
          // },
          {
            name: "Pending Companies",
            href: "/admin/pending",
            icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
            badge: pendingCompaniesCount ? pendingCompaniesCount > 0 ? pendingCompaniesCount : null : null,
          },
          {
            name: "All Companies",
            href: "/admin/companies",
            icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4",
            badge: null,
          },
          {
            name: "All Posts",
            href: "/admin/posts",
            icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
            badge: null,
          },
          {
            name: "All Users",
            href: "/admin/users",
            icon: "M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695L12 22.5l-7.812-1.7a.75.75 0 01-.437-.695z",
            badge: null,
          },
          // {
          //   name: "External Candidates",
          //   href: "/admin/external-candidates",
          //   icon: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z",
          //   badge: null,
          // },
          {
            name: "Inquiries",
            href: "/admin/Inquiries",
            icon: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9",
            badge: null,
          },
        ];
      
      default:
        return [];
    }
  };

  const navigationItems = getNavigationItems();

  return (
    <>
      {/* Sidebar */}
      <aside
        className={`
        fixed lg:static inset-y-0 left-0 z-50 w-64 min-h-screen bg-white transform transition-all duration-300 ease-in-out no-horizontal-scroll
        ${isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        shadow-xl lg:shadow-none flex-shrink-0 max-w-full
      `}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="px-3 sm:px-4 lg:px-5">
            {/* Mobile close button */}
            <div className="flex justify-end lg:hidden mb-4 sm:mb-6">
              <button
                onClick={onClose}
                className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none rounded-xl hover:bg-gray-100 transition-all duration-300"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* User Info */}
            <div className = 'sm:pt-4 lg:pt-20'></div>
            {/* <div className="flex items-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary-lightGreen to-primary-teaGreen rounded-2xl flex items-center justify-center shadow-lg">
                <span className="text-primary-black font-bold text-base sm:text-lg">
                  {user?.firstName?.charAt(0) || user?.email?.charAt(0) || "U"}
                </span>
              </div>
              <div className="ml-3 sm:ml-4 flex-1 min-w-0">
                <p className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                  {user?.firstName || "User"}
                </p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
                <div className="flex items-center mt-1">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-xs text-gray-500 ml-2">Online</span>
                </div>
              </div>
            </div> */}
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 sm:px-3 py-2 sm:py-3 space-y-1 overflow-y-auto scrollbar-hide">
            {navigationItems.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={onClose}
                  className={`group flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 text-sm font-medium rounded-xl transition-all duration-300 relative hover:scale-[1.02] ${
                    isActive
                      ? "bg-gradient-to-r from-primary-lightGreen to-primary-teaGreen text-primary-black shadow-lg scale-[1.02]"
                      : "text-gray-700 hover:bg-primary-teaGreen/30 hover:text-primary-black"
                  }`}
                >
                  <div className="flex items-center min-w-0 flex-1">
                    <div
                      className={`p-1.5 sm:p-2 rounded-lg transition-all duration-300 flex-shrink-0 ${
                        isActive
                          ? "bg-primary-black/15"
                          : "group-hover:bg-primary-lightGreen/25"
                      }`}
                    >
                      <svg
                        className="h-4 w-4 sm:h-5 sm:w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d={item.icon}
                        />
                      </svg>
                    </div>
                    <span className="ml-2 sm:ml-3 truncate text-xs sm:text-sm">
                      {item.name}
                    </span>
                  </div>

                  {item.badge && (
                    <span
                      className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-semibold rounded-full flex-shrink-0 ml-2 ${
                        isActive
                          ? "bg-primary-black/20 text-primary-black"
                          : "bg-primary-lightGreen text-primary-black"
                      }`}
                    >
                      {item.badge}
                    </span>
                  )}

                  {isActive && (
                    <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-6 sm:h-8 bg-primary-black rounded-r-full"></div>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="p-2 sm:p-3">
            <div className="bg-gradient-to-r from-primary-teaGreen/20 to-primary-lightGreen/20 from-primary-teaGreen/10 to-primary-lightGreen/10 rounded-lg p-2 sm:p-3 border border-primary-teaGreen/20">
              <div className="flex items-center">
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-primary-lightGreen rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-3 h-3 sm:w-4 sm:h-4 text-primary-black"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="ml-2 sm:ml-3 flex-1 min-w-0 cursor-pointer" onClick={() => window.open(`${window.location.origin}/contact`, "_blank")}>
                  <p className="text-xs font-semibold text-gray-900">
                    Need Help?
                  </p>
                  <p className="text-xs text-gray-600 truncate">
                    Click here for Support
                  </p>
                </div>
              </div>
            </div>

            {/* Version info - hidden on mobile */}
            <div className="mt-2 text-center hidden sm:block">
              <p className="text-xs text-gray-400 text-gray-500">
                v1.0.0 • Dhoondh
              </p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
